import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import Loading from "../../../../components/Loading";
import { formatDateForAPI } from "../../../Rajasthan/Utility/const";
import { useSelector, useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../../store/state/FilterLoadingState";
import {
  fetchExchangeSlotData,
  getExchangeAnalysisData,
  getCollectiveMetrics,
} from "../../../../Rest_api/RestApi";
import { ExchangeMetrics } from "../../../../models/metrics";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { renderHourTick, renderQuarterTick } from "../../../../components/recharts/components";
import {
  ChartTable,
} from "../../../../components/table/SummaryTable";
import LegendSummarySlotTable from "../../../../components/table/SummaryTableSlot";
import { BrushStart, ChartType, LegendKey } from "../../../../models/chart_model";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import FilterMenu from "../../../../components/FilterMenu";
import Modal from "../../../../components/Model";
import BuyerSellerChart from "./TopBuyersSellersPopup";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReactComponent as Info } from "../../../../icons/Exchange-Analysis.svg"

const DataTabs = ["comparative", "cumulative"];

interface ExchangeAnalysisDataItem {
  x_axis: string;
  second_x_axis: string | null;
  price: number;
  prev_year_price: number | null;
  rolling_actual_30_day_avgs: number | null;
  prev_year_rolling_actual_30_day_avgs: number | null;
  gna_forecasted_price: number | null;
  exchange__name: string;
  product__name: string;
  mcv_mw: number;
  mcv_mwh: number;
  prev_year_mcv_mw: number | null;
  prev_year_mcv_mwh: number | null;
  rolling_actual_30_day_avgs_mcv_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_mcv_mw: number | null;
  purchase_bids_mw: number;
  purchase_bids_mwh: number;
  prev_year_purchase_bids_mw: number | null;
  prev_year_purchase_bids_mwh: number | null;
  rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  sell_bids_mw: number;
  sell_bids_mwh: number;
  prev_year_sell_bids_mw: number | null;
  prev_year_sell_bids_mwh: number | null;
  rolling_actual_30_day_avgs_sell_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_sell_bids_mw: number | null;
}

export interface ExchangeAnalysisParams {
  interval: "slot" | "day" | "week" | "month";
  price_type: "simple_avg" | "wt_avg";
  exchange_name: string; // "IEX" | "PXIL" | "HPX" | "all"
  product_name: string; // "DAM" | "RTM" | "GDAM" | "HPDAM" | "all"
  start_date?: string;
  end_date?: string;
  function?: string;
  blocks: string[] | number[];
}

export interface ExchangeAnalysisData {
  x_axis: string;
  second_x_axis: string | null;
  price: number;
  prev_year_price: number | null;
  rolling_actual_30_day_avgs: number | null;
  prev_year_rolling_actual_30_day_avgs: number | null;
  gna_forecasted_price: number | null;
  exchange__name: string;
  product__name: string;
  mcv_mw: number;
  mcv_mwh: number;
  prev_year_mcv_mw: number | null;
  prev_year_mcv_mwh: number | null;
  rolling_actual_30_day_avgs_mcv_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_mcv_mw: number | null;
  purchase_bids_mw: number;
  purchase_bids_mwh: number;
  prev_year_purchase_bids_mw: number | null;
  prev_year_purchase_bids_mwh: number | null;
  rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  sell_bids_mw: number;
  sell_bids_mwh: number;
  prev_year_sell_bids_mw: number | null;
  prev_year_sell_bids_mwh: number | null;
  rolling_actual_30_day_avgs_sell_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_sell_bids_mw: number | null;
}

const variableFieldMapping: Record<string, Record<string, string>> = {
  price: {
    prev_year: "prev_year_price",
    rolling_avg: "rolling_actual_30_day_avgs",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs",
  },
  mcv: {
    prev_year: "prev_year_mcv",
    rolling_avg: "rolling_actual_30_day_avgs_mcv",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_mcv",
  },
  purchase_bids: {
    prev_year: "prev_year_purchase_bids",
    rolling_avg: "rolling_actual_30_day_avgs_purchase_bids",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_purchase_bids",
  },
  sell_bids: {
    prev_year: "prev_year_sell_bids",
    rolling_avg: "rolling_actual_30_day_avgs_sell_bids",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_sell_bids",
  },
};

const chartSuffixes = [
  {
    suffix: "price",
    displayName: "MCP",
    stackSuffix: "price",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "sell_bids",
    displayName: "Sell Bid",
    stackSuffix: "sell_bids",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "purchase_bids",
    displayName: "Purchase Bid",
    stackSuffix: "purchase_bids",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "mcv",
    displayName: "MCV",
    stackSuffix: "mcv",
    chartType: ChartType.Bar,
    unit: "MW/MWh",
    yAxisId: "left",
  },
];

const typeFilterMetrics = chartSuffixes;

const variableFilterOptions = [
  { key: "actual", label: "Actual" },
  { key: "prev_year", label: "Prev Year Actual" },
  { key: "rolling_avg", label: "30D Roll Avg" },
  { key: "prev_year_rolling_avg", label: "Prev Year 30D Roll Avg" },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "slot" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: true,
  },
  {
    label: "Date",
    key: "day" as const,
    values: ["x_axis"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month" as const,
    values: ["x_axis"],
    isTime: false,
  },
];

const SLOT_INTERVAL =
  Intervals.find((interval) => interval.key === "slot") || Intervals[0];

const PRICE_TYPE_OPTIONS = [
  { key: "simple_avg" as const, label: "Simple Avg" },
  { key: "wt_avg" as const, label: "Weighted Avg" },
];

const UNIT_OPTIONS = ["MW", "MWh"];

const exchanges = ["IEX", "PXIL", "HPX"];
const products = ["DAM", "RTM", "GDAM", "HPDAM"];

const ExchangeAnalysis = () => {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [activefunctionTab, setActivefunctionTab] = useState(DataTabs[0]);
  const [rawData, setRawData] = useState<ExchangeAnalysisDataItem[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [summaryIntervalData, setSummaryIntervalData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedPriceType, setSelectedPriceType] = useState(PRICE_TYPE_OPTIONS[1]);
  const [selectedUnit, setSelectedUnit] = useState(UNIT_OPTIONS[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedTypeMetrics, setSelectedTypeMetrics] = useState<any[]>([
    typeFilterMetrics[0],
  ]);
  const [selectedVariableOptions, setSelectedVariableOptions] = useState<string[]>([
    "actual",
    "prev_year",
    "rolling_avg",
    "prev_year_rolling_avg",
  ]);
  const [loading, setLoading] = useState(false);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(["IEX"]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>(["DAM"]);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<any>({});
  const [modalHeader, setModalHeader] = useState<any>({});
  const [metrics, setMetrics] = useState<ExchangeMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [selectedIntervalRawData, setSelectedIntervalRawData] = useState<ExchangeAnalysisDataItem[] | null>(null);
  const dispatch = useDispatch();

  const isChartView = dataTab?.label === "Chart";
  const effectiveInterval = isChartView ? selectedInterval : SLOT_INTERVAL;

  const fetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? "all"
          : selectedExchanges.join(",");

      const productName =
        selectedProducts.length === products.length
          ? "all"
          : selectedProducts.join(",");

      const data = await getExchangeAnalysisData({
        interval: effectiveInterval.key,
        price_type: selectedPriceType.key,
        exchange_name: exchangeName,
        product_name: productName,
        start_date: startDateStr,
        end_date: endDateStr,
        function: activefunctionTab,
      } as ExchangeAnalysisParams);

      setRawData(data);

      if (effectiveInterval.key !== selectedInterval.key) {
        try {
          const intervalData = await getExchangeAnalysisData({
            interval: selectedInterval.key,
            price_type: selectedPriceType.key,
            exchange_name: exchangeName,
            product_name: productName,
            start_date: startDateStr,
            end_date: endDateStr,
            function: activefunctionTab,
          } as ExchangeAnalysisParams);
          setSelectedIntervalRawData(intervalData);
        } catch (intervalError) {
          console.error("Error fetching selected interval data:", intervalError);
          setSelectedIntervalRawData(null);
        }
      } else {
        setSelectedIntervalRawData(data);
      }

      if (effectiveInterval.key === "slot") {
        setUniqueBlock(
          Array.from(
            new Set(data.map((item: ExchangeAnalysisDataItem) => item.x_axis))
          )
        );
      } else {
        setUniqueBlock([]);
      }
    } catch (error) {
      console.error("Error fetching exchange analysis data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
      dispatch(setLoadingFilterName(""));
    }
  }, [
    DefaultDates,
    activeTab,
    effectiveInterval.key,
    selectedInterval.key,
    selectedPriceType.key,
    activefunctionTab,
    dataTab,
    selectedExchanges,
    selectedProducts,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? undefined
          : selectedExchanges.length > 0
            ? selectedExchanges
            : undefined;

      const productName =
        selectedProducts.length === products.length
          ? undefined
          : selectedProducts.length > 0
            ? selectedProducts
            : undefined;

      const metricsData = await getCollectiveMetrics({
        startDate: startDateStr,
        endDate: endDateStr,
        exchangeName: exchangeName,
        productName: productName,
        blocks: selectedBlock.map((block) => block.value),

      });

      setMetrics(metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab,selectedBlock, selectedExchanges, selectedProducts]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const processDataForInterval = useCallback(
    (sourceData: ExchangeAnalysisDataItem[], intervalConfig: (typeof Intervals)[number]) => {
      let filteredData = [...sourceData];

      if (activefunctionTab === "comparative") {
        if (
          selectedExchanges.length > 0 &&
          selectedExchanges.length < exchanges.length
        ) {
          filteredData = filteredData.filter((item) =>
            selectedExchanges.includes(item.exchange__name)
          );
        }

        if (
          selectedProducts.length > 0 &&
          selectedProducts.length < products.length
        ) {
          filteredData = filteredData.filter((item) =>
            selectedProducts.includes(item.product__name)
          );
        }
      }

      if (selectedBlock.length > 0 && intervalConfig.key === "slot") {
        filteredData = filteredData.filter((item) =>
          selectedBlock.map((block) => block.value).includes(item.x_axis)
        );
      }

      const groupedData: any = {};

      filteredData.forEach((item) => {
        let key: string;
        if ((intervalConfig.isTime || intervalConfig.values.includes("second_x_axis")) && item.second_x_axis) {
          key = `${item.second_x_axis}_${item.x_axis}`;
        } else {
          key = item.x_axis;
        }

        if (!groupedData[key]) {
          groupedData[key] = {
            x_axis: item.x_axis,
            second_x_axis: item.second_x_axis,
          };
        }

        const exchangeName = item.exchange__name || "";
        const productName = item.product__name || "";
        const prefix = activefunctionTab === "cumulative"
          ? "Cumulative"
          : exchangeName && productName
          ? `${exchangeName}_${productName}`
          : "Cumulative";

        selectedTypeMetrics.forEach((typeMetric) => {
          let actualSuffix = typeMetric.suffix;
          if (typeMetric.unit === "MW/MWh") {
            actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
          }

          const dataKey = `${prefix}_${actualSuffix}`;

          if (selectedVariableOptions.includes("actual")) {
            groupedData[key][dataKey] =
              item[actualSuffix as keyof ExchangeAnalysisDataItem];
          }

          selectedVariableOptions.forEach((variableKey) => {
            if (variableKey === "actual") {
              return;
            }

            const variableFieldName = variableFieldMapping[typeMetric.suffix]?.[variableKey];
            if (variableFieldName) {
              let actualVariableSuffix = variableFieldName;
              if (typeMetric.unit === "MW/MWh") {
                actualVariableSuffix = `${variableFieldName}_${selectedUnit.toLowerCase()}`;
              }

              const variableDataKey = `${prefix}_${actualVariableSuffix}`;
              groupedData[key][variableDataKey] =
                item[actualVariableSuffix as keyof ExchangeAnalysisDataItem];
            }
          });
        });

        if (item.mcv_mw !== undefined) {
          groupedData[key][`${prefix}_mcv_mw`] = item.mcv_mw;
        }
        if (item.mcv_mwh !== undefined) {
          groupedData[key][`${prefix}_mcv_mwh`] = item.mcv_mwh;
        }
      });

      const formattedChartData = Object.values(groupedData);

      const keys = activefunctionTab === "cumulative"
        ? ["Cumulative"]
        : Array.from(
            new Set(
              filteredData.map((item) => {
                const exchangeName = item.exchange__name || "";
                const productName = item.product__name || "";
                return exchangeName && productName
                  ? `${exchangeName}_${productName}`
                  : "Cumulative";
              })
            )
          );

      return {
        chartData: formattedChartData,
        tableData: filteredData,
        chartKeys: keys,
      };
    },
    [
      activefunctionTab,
      selectedBlock,
      selectedExchanges,
      selectedProducts,
      selectedTypeMetrics,
      selectedVariableOptions,
      selectedUnit,
    ]
  );

  useEffect(() => {
    if (!rawData.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setSummaryIntervalData([]);
      return;
    }

    const primaryProcessed = processDataForInterval(rawData, effectiveInterval);
    setChartData(primaryProcessed.chartData);
    setChartKeys(primaryProcessed.chartKeys);
    setTableData(primaryProcessed.tableData);

    const intervalSource = selectedIntervalRawData || rawData;
    const intervalProcessed = processDataForInterval(
      intervalSource,
      selectedInterval
    );
    setSummaryIntervalData(intervalProcessed.chartData);
  }, [
    rawData,
    selectedIntervalRawData,
    effectiveInterval,
    selectedInterval,
    processDataForInterval,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    yAxisId: string
  ) => {
    let actualUnit = unit;
    if (unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }

    return {
      dataKey: `${key}_${suffix}`,
      name: `${key.replace(/_/g, " ")} ${displayName}`,
      legendColor: undefined,
      stroke: undefined,
      fill: undefined,
      type: chartType,
      unit: actualUnit,
      yAxisId: yAxisId,
      stackId:
        stackSuffix !== undefined && chartType === ChartType.Bar
          ? stackSuffix
          : undefined,
    };
  };

  function getLegends() {
    const legends: any[] = [];

    chartKeys.forEach((key, index) => {
      selectedTypeMetrics.forEach((typeMetric) => {
        let actualSuffix = typeMetric.suffix;
        if (typeMetric.unit === "MW/MWh") {
          actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
        }

        if (selectedVariableOptions.includes("actual")) {
          legends.push(
            createChartObject(
              key,
              index,
              actualSuffix,
              typeMetric.displayName,
              typeMetric.stackSuffix,
              typeMetric.chartType,
              typeMetric.unit,
              typeMetric.yAxisId
            )
          );
        }

        selectedVariableOptions.forEach((variableKey) => {
          if (variableKey === "actual") return; 
          
          const variableOption = variableFilterOptions.find(opt => opt.key === variableKey);
          const variableFieldName = variableFieldMapping[typeMetric.suffix]?.[variableKey];
          
          if (variableFieldName && variableOption) {
            let actualVariableSuffix = variableFieldName;
            if (typeMetric.unit === "MW/MWh") {
              actualVariableSuffix = `${variableFieldName}_${selectedUnit.toLowerCase()}`;
            }

            const variableLabelWithType = `${variableOption.label} (${typeMetric.displayName})`;

            legends.push(
              createChartObject(
                key,
                index,
                actualVariableSuffix,
                variableLabelWithType,
                variableFieldName,
                ChartType.Line,
                typeMetric.unit,
                typeMetric.yAxisId
              )
            );
          }
        });
      });
    });

    return legends;
  }

  const memoizedLegends = useMemo(
    () => getLegends(),
    [chartKeys, selectedTypeMetrics, selectedVariableOptions, selectedUnit]
  );

  const priceLegends = useMemo(
    () =>
      memoizedLegends.map((legend: LegendKey) =>
        legend.unit === "Rs/KWh" ? legend : null
      ),
    [memoizedLegends]
  );

  const xDataKey = "x_axis";

  const viewExtraKeys = effectiveInterval.isTime
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const viewExtraLabels = effectiveInterval.isTime
    ? ["Time Slot", effectiveInterval.key === "slot" ? "Date" : "Month"]
    : ["Date"];

  const summaryExtraKeys = selectedInterval.isTime
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const summaryExtraLabels = selectedInterval.isTime
    ? ["Time Slot", selectedInterval.key === "slot" ? "Date" : "Month"]
    : ["Date"];

  const tableUnits: Record<string, string> = {};
  chartSuffixes.forEach((curr) => {
    let actualUnit = curr.unit;
    if (curr.unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    tableUnits[curr.suffix] = actualUnit;
    
    Object.values(variableFieldMapping[curr.suffix] || {}).forEach((fieldName) => {
      tableUnits[fieldName] = actualUnit;
    });
  });

  const tablePar = {
    legends: memoizedLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys: viewExtraKeys,
    extraLabels: viewExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Exchange Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate,
    }),
    customUnits: tableUnits,
    showTotal: false
  };

  function secondDataKey() {
    if (effectiveInterval.values.includes("second_x_axis")) {
      return "second_x_axis";
    } else {
      return undefined;
    }
  }

  async function handleBarClick(item: any) {
    if (activefunctionTab === "cumulative") {
      return;
    }

    setLoading(true);
    try {
      const dataIndex = item.dataIndex;
      const clickedData = chartData[dataIndex];
      const seriesName = item.seriesName.split(" ");

      const exchange = seriesName[0];
      const product = seriesName[1];
      const slot = effectiveInterval.key === "slot" ? item.name : "no-slot";
      const date = clickedData.second_x_axis || clickedData.x_axis;

      const response = await fetchExchangeSlotData({
        exchange: exchange,
        product: product,
        slot: slot,
        date: date,
      });

      setModalHeader({
        exchange: exchange,
        product: product,
        slot: effectiveInterval.key === "slot" ? slot : "All Slots",
        date: date,
      });
      setModalData(response);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching slot data:", error);
    } finally {
      setLoading(false);
    }
  }

  const formatMetricValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "N/A";
    return typeof value === "number" ? value.toFixed(3) : "N/A";
  };

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Interval"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
                setSelectedBlock([]);
              }}
            />

            <FilterMenu
              title="Unit"
              items={UNIT_OPTIONS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                setSelectedUnit(UNIT_OPTIONS[index]);
              }}
            />
            
            <FilterMenu
              title="Function"
              items={DataTabs.map(tab => tab.charAt(0).toUpperCase() + tab.slice(1))}
              selectedItems={[activefunctionTab.charAt(0).toUpperCase() + activefunctionTab.slice(1)]}
              onSelect={(item, index) => {
                setActivefunctionTab(DataTabs[index]);
              }}
            />

            <FilterMenu
              title="Exchange"
              items={exchanges}
              selectedItems={selectedExchanges}
              setSelectedItems={(items: string[]) => {
                setSelectedExchanges(items);
              }}
            />

            <FilterMenu
              title="Product"
              items={products}
              selectedItems={selectedProducts}
              setSelectedItems={(items: string[]) => {
                setSelectedProducts(items);
              }}
            />

            <FilterMenu
              title="Type"
              items={typeFilterMetrics.map((item) => item.displayName)}
              selectedItems={selectedTypeMetrics.map((item) => item.displayName)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                const newSelectedMetrics = typeFilterMetrics.filter((item) => {
                  const isSelected = selectedItems.includes(item.displayName);
                  return isSelected;
                });
                setSelectedTypeMetrics(newSelectedMetrics);
              }}
            />
            <FilterMenu
              title="Cumulation"
              items={PRICE_TYPE_OPTIONS.map((item) => item.label)}
              selectedItems={[selectedPriceType.label]}
              onSelect={(item, index) => {
                setSelectedPriceType(PRICE_TYPE_OPTIONS[index]);
              }}
            />
            <FilterMenu
              title="Variable"
              items={variableFilterOptions.map((item) => item.label)}
              selectedItems={variableFilterOptions
                .filter((opt) => selectedVariableOptions.includes(opt.key))
                .map((opt) => opt.label)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                const newSelectedKeys = variableFilterOptions
                  .filter((opt) => selectedItems.includes(opt.label))
                  .map((opt) => opt.key);
                
                setSelectedVariableOptions(newSelectedKeys);
              }}
            />

          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          <div className="gas-chart-container">
            <div className="gas-heading gap-x-10">
              <h2 className="chartHeading w-60">Collective</h2>
              <div className="flex gap-x-6 justify-end">
                
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Select Slot"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
               
              </div>
            </div>

            {/* Metrics Cards - Only show in Summary view */}
            {metrics && dataTab.label === "Summary" && (
              <div className="mt-2 mb-3 bg-white rounded-xl p-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  {/* Card 1: Weighted Avg Price */}
                  <div className="flex flex-row justify-start items-center gap-8 bg-white p-2 rounded-2xl transform hover:scale-105 transition duration-300 border border-gray-200">
                    <div className="text-[#A3AED0] text-lg mb-1">
                      <Info className="w-25 h-25  text-slate-300" /> </div>

                    <div className="text-start "><h3 className="text-xl text-[#A3AED0] mb-0.5">

                      Total Wgt Avg Price
                    </h3>
                      <p className="text-xl font-bold text-[#2B3674]">
                        {formatMetricValue(metrics.weighted_avg_price.value)}{" "}
                        <span className="text-lg text-[#2B3674]">
                          {metrics.weighted_avg_price.unit}
                        </span>
                      </p></div>
                  </div>

                  {/* Card 2: Avg Volume */}
                  <div className="flex flex-row justify-start items-center gap-8 bg-white p-2 rounded-2xl transform hover:scale-105 transition duration-300 border border-gray-200">
                    <div className="text-[#F4A261] text-lg mb-1">
                      <Info className="w-25 h-25  text-slate-300" />
                    </div>
                   <div className="text-start"> <h3 className="text-xl text-[#A3AED0] mb-0.5">
                      Average Volume
                    </h3>
                    <p className="text-xl font-bold text-[#2B3674]">
                      {formatMetricValue(metrics.avg_volume.value)}{" "}
                      <span className="text-lg text-[#2B3674]">
                        {metrics.avg_volume.unit}
                      </span>
                    </p></div>
                  </div>

                  {/* Card 3: Total Volume */}
                  <div className="flex flex-row justify-start items-center gap-8 bg-white p-2 rounded-2xl transform hover:scale-105 transition duration-300 border border-gray-200">
                    <div className="text-[#F4A261] text-lg mb-1">
                      <Info className="w-25 h-25  text-slate-300" />
                    </div>
                   <div className="text-start"> <h3 className="text-xl text-[#A3AED0] mb-0.5">
                      Total Volume
                    </h3>
                    <p className="text-xl font-bold text-[#2B3674]">
                      {formatMetricValue(metrics.total_volume_mu.value)}{" "}
                      <span className="text-xl text-[#2B3674]">
                        {metrics.total_volume_mu.unit}
                      </span>
                    </p></div>
                  </div>
                </div>
              </div>
            )}

            {dataTab.label === "Chart" && (
              <ReMixChart
                onBarClick={(item: any) => {
                  handleBarClick(item);
                }}
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={effectiveInterval.isTime}
                onlyTitle={true}
                legends={memoizedLegends}
                secondYAxisDomain={[0,10]}
                secondYAxisLabel="Rs/KWh"
                yAxisLabel={selectedUnit}
                xDataKey={xDataKey}
                xTick={
                  effectiveInterval.key === "slot" && selectedBlock.length === 0
                    ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                    : effectiveInterval.key === "week"
                      ? renderQuarterTick
                      : undefined
                }
                secondXDataKey={secondDataKey()}
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummarySlotTable
                legends={memoizedLegends}
                avgData={chartData}
                minMaxData={
                  summaryIntervalData.length > 0 ? summaryIntervalData : chartData
                }
                avgTableData={TableData}
                extraKeys={summaryExtraKeys}
                extraLabels={summaryExtraLabels}
                unit={selectedUnit}
                priceLegends={priceLegends}
                volumeUnitKey={selectedUnit.toLowerCase()}
                block_key={selectedInterval.key === "slot" ? "x_axis" : undefined}
                showWtdAvg={true}
                priceType={selectedPriceType.key}
                showTotal={false}
              />
            )}
            {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-2 justify-between gap-5 ">
              <p className="gna-footer-text col-span-1">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
      <Modal
        height="calc(100vh - 100px)"
        title={`Top Buyers & Sellers (${modalHeader.exchange} - ${modalHeader.product} - ${modalHeader.slot} Time Block - ${modalHeader.date})`}
        description=""
        width="90%"
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setModalData({});
        }}
        children={
          <>
            <div
              style={{ height: "calc(100vh - 200px)" }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 "
            >
              {/* Buyers Section */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-gray-800"></div>
                      <h4 className="text-base font-semibold text-gray-800">
                        Top Buyers
                      </h4>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-200 px-3 py-1 rounded-full">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                      {modalData.buyer?.filter(
                        (b: { value: number }) => b.value > 0
                      ).length || 0}{" "}
                      Active
                    </div>
                  </div>

                  <div className="mt-3 ps-6">
                    <div className="text-sm text-gray-600 font-medium">
                      Total Volume
                    </div>
                    <div className="text-lg font-bold text-gray-800">
                      {modalData.buyer
                        ?.reduce(
                          (sum: any, item: { value: any }) => sum + item.value,
                          0
                        )
                        .toFixed(2)}{" "}
                      MWh
                    </div>
                  </div>
                </div>

                <div className="p-4 h-[calc(100vh-320px)] overflow-x-hidden overflow-y-auto">
                  <BuyerSellerChart
                    data={modalData.buyer || []}
                    showLegend={false}
                    type="buyer"
                    theme="light"
                  />
                </div>
              </div>

              {/* Sellers Section */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-[#34656D] px-6 py-4 border-b ">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-white"></div>
                      <h4 className="text-base font-semibold text-white">
                        Top Sellers
                      </h4>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300 bg-gray-700 px-3 py-1 rounded-full">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {modalData.seller?.filter(
                        (s: { value: number }) => s.value > 0
                      ).length || 0}{" "}
                      Active
                    </div>
                  </div>

                  <div className="mt-3 ps-6">
                    <div className="text-sm text-gray-300 font-medium">
                      Total Volume
                    </div>
                    <div className="text-lg font-bold text-white">
                      {modalData.seller
                        ?.reduce(
                          (sum: any, item: { value: any }) => sum + item.value,
                          0
                        )
                        .toFixed(2)}{" "}
                      MWh
                    </div>
                  </div>
                </div>

                <div className="p-4 h-[calc(100vh-320px)] overflow-x-hidden overflow-y-auto">
                  <BuyerSellerChart
                    data={modalData.seller || []}
                    showLegend={false}
                    type="seller"
                    theme="light"
                  />
                </div>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};

export default ExchangeAnalysis;